import React, { useMemo, useState } from 'react';
import { X, Info, ExternalLink, CheckCircle2, RefreshCcw, Settings, Link2, ShieldCheck, Copy, HelpCircle } from 'lucide-react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { validateBitrixDomain, getBitrixStatus, BitrixStatusResponse, ValidateDomainResponse, getOpenChannels, OpenChannelsResponse, getOpenChannelLink, saveOpenChannelLink, SaveOpenChannelLinkResponse, setOpenChannelActive, uninstallBitrix } from '../services/bitrixService';
import { enableApiIntegration, deleteApiIntegration, setApiWebhookUrl, getApiIntegrationStatus, rotateApiKey, setApiEnabled, rotateWebhookSecret, listWebhookLogs, type EnableApiIntegrationResponse, type DeleteApiIntegrationResponse, type SetWebhookUrlResponse, type ListWebhookLogsResponse } from '../services/apiIntegrationService';
import { getChannels } from '../services/channelService';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface Integration {
  id: string;
  name: string;
  img: string;
  isAvailable: boolean;
  description?: string;
}

const CRMIntegrations: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bitrixUrl, setBitrixUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedLine, setSelectedLine] = useState<string>('');
  const [selectedChannelId, setSelectedChannelId] = useState<number | ''>('');
  const [pendingChange, setPendingChange] = useState<{ message: string; line: string; channelId: number } | null>(null);
  const isManualLineChangeRef = React.useRef(false);
  const isManualChannelChangeRef = React.useRef(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [showApiModal, setShowApiModal] = useState(false);
  const [apiInfo, setApiInfo] = useState<{ apiKey: string; webhookUrl?: string; webhookSecret?: string } | null>(null);
  const [apiIsEnabled, setApiIsEnabled] = useState<boolean>(false);
  const [webhookInput, setWebhookInput] = useState('');
  const [webhookMsg, setWebhookMsg] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsEventType, setLogsEventType] = useState<string>('');

  // Estado de instalación
  const { data: statusResp, isLoading: statusLoading } = useQuery<BitrixStatusResponse>({
    queryKey: ['bitrixStatus'],
    queryFn: () => getBitrixStatus(),
  });

  const bitrixStatus = statusResp?.data;
  const isInstalled = !!bitrixStatus?.installed;

  // Estado local para API
  const apiExists = apiInfo != null;
  // Masked-gate flags: the backend returns the literal MASKED_SECRET_PLACEHOLDER
  // ('***') sentinel on every read after the first reveal. Copy/Show controls
  // and the input type must reflect that this is not the real secret value.
  const isApiKeyMasked = apiInfo?.apiKey === '***';
  const isWebhookSecretMasked = apiInfo?.webhookSecret === '***';

  // Consultar estado de la API al cargar la vista para persistir tras refresh
  const { data: apiStatusResp, isLoading: apiStatusLoading } = useQuery({
    queryKey: ['apiIntegrationStatus'],
    queryFn: getApiIntegrationStatus,
  });

  React.useEffect(() => {
    const st = apiStatusResp?.data;
    if (st && st.apiKey) {
      setApiInfo({ apiKey: st.apiKey || '—', webhookUrl: st.webhookUrl, webhookSecret: st.webhookSecret });
      setApiIsEnabled(!!st.enabled);
      if (st.webhookUrl) setWebhookInput(st.webhookUrl);
    } else {
      setApiInfo(null);
      setApiIsEnabled(false);
    }
  }, [apiStatusResp?.data]);

  const integrations: Integration[] = [
    {
      id: 'bitrix24',
      name: 'Bitrix24',
      description: t('connectBitrix24Description') || 'Connect your Bitrix24 CRM to sync contacts and leads',
      img: './Bitrix24.svg',
      isAvailable: true,
    },
    {
      id: 'whatsapp-api',
      name: 'WhatsApp API',
      description: t('sendViaApiReceiveWebhooks') || 'Send via API and receive via webhooks',
      img: '/api.svg',
      isAvailable: true,
    },
  ];

  const handleConnect = (integrationId: string) => {
    if (integrationId === 'bitrix24') {
  if (apiExists) return; // bloqueado por API existente
      setIsModalOpen(true);
    }
    if (integrationId === 'whatsapp-api') {
      if (isInstalled) return; // bloqueado por Bitrix
      setShowApiModal(true);
    }
  };

  // Mutación para validar dominio
  const { mutate: doValidate, isPending: validating } = useMutation<ValidateDomainResponse, unknown, { url: string; clientId: string; clientSecret: string }>({
    mutationFn: ({ url, clientId, clientSecret }) => validateBitrixDomain({ url, clientId, clientSecret }),
    onSuccess: async (resp) => {
      setError(null);
      const data = resp?.data;
      if (data?.requiresInstall) {
        setMessage(data.message || t('installAppAndRetry'));
      } else if (data?.installed) {
        setMessage(data.message || t('bitrix24ActivatedSuccessfully'));
        setIsModalOpen(false);
        setBitrixUrl('');
        await queryClient.invalidateQueries({ queryKey: ['bitrixStatus'] });
      } else {
        setMessage(t('validationCompleted'));
      }
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      const msg = err?.response?.data?.message || err?.message || t('errorValidatingDomain');
      setError(msg);
    },
  });

  const handleContinue = () => {
    if (bitrixUrl.trim() && clientId.trim() && clientSecret.trim()) {
      doValidate({ url: bitrixUrl.trim(), clientId: clientId.trim(), clientSecret: clientSecret.trim() });
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setBitrixUrl('');
    setClientId('');
    setClientSecret('');
  };

  // Enable/Disable API integration
  const { mutateAsync: doEnableApi, isPending: enablingApi } = useMutation<EnableApiIntegrationResponse, unknown, void>({
    mutationFn: enableApiIntegration,
    onSuccess: async (resp) => {
      const apiKey = resp?.data?.apiKey;
      const webhookUrl = resp?.data?.webhookUrl;
      setApiInfo(apiKey ? { apiKey, webhookUrl, webhookSecret: resp?.data?.webhookSecret } : { apiKey: '—', webhookUrl, webhookSecret: resp?.data?.webhookSecret });
  setApiIsEnabled(true);
      setShowApiModal(false);
  if (webhookUrl) setWebhookInput(webhookUrl);
    },
  });

  const { mutateAsync: doDeleteApi, isPending: deletingApi } = useMutation<DeleteApiIntegrationResponse, unknown, void>({
    mutationFn: deleteApiIntegration,
    onSuccess: async () => {
      setApiInfo(null);
  setApiIsEnabled(false);
      setWebhookInput('');
      setWebhookMsg(null);
    },
  });

  const { mutateAsync: doSetWebhook, isPending: savingWebhook } = useMutation<SetWebhookUrlResponse, unknown, string | null>({
    mutationFn: (url: string | null) => setApiWebhookUrl(url),
    onSuccess: (resp) => {
      const url = resp?.data?.webhookUrl || '';
      setWebhookMsg(url ? t('webhookSavedSuccessfully') : t('webhookRemoved'));
      setApiInfo((prev) => (prev ? { ...prev, webhookUrl: url || undefined } : prev));
      if (!url) setWebhookInput('');
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      const msg = err?.response?.data?.message || err?.message || t('errorSavingWebhook');
      setWebhookMsg(msg);
    }
  });

  // Rotate key
  const { mutateAsync: doRotateKey, isPending: rotatingKey } = useMutation({
    mutationFn: rotateApiKey,
    onSuccess: (resp: { data?: { apiKey?: string } }) => {
      const newKey = resp?.data?.apiKey || '';
      setApiInfo((prev) => (prev ? { ...prev, apiKey: newKey } : prev));
    },
  });

  const { mutateAsync: doRotateWebhookSecret, isPending: rotatingSecret } = useMutation({
    mutationFn: rotateWebhookSecret,
    onSuccess: (resp: { data?: { webhookSecret?: string } }) => {
      const newSecret = resp?.data?.webhookSecret || '';
      setApiInfo((prev) => (prev ? { ...prev, webhookSecret: newSecret } : prev));
    },
  });

  const { data: logsResp } = useQuery<ListWebhookLogsResponse>({
    queryKey: ['apiWebhookLogs', logsPage, logsEventType],
    queryFn: () => listWebhookLogs({ page: logsPage, pageSize: 20, eventType: logsEventType || undefined }),
    enabled: apiExists,
  });

  // Toggle enabled
  const { mutateAsync: doSetEnabled, isPending: togglingEnabled } = useMutation({
    mutationFn: (enabled: boolean) => setApiEnabled(enabled),
    onSuccess: (resp: { data?: { enabled?: boolean } }) => {
      const enabled = !!resp?.data?.enabled;
      if (!enabled) {
        // Keep record but mark disabled (optional UX: could hide the section)
  setWebhookMsg(t('integrationDisabled'));
  // Reflejar en la UI inmediatamente sin eliminar la integración
  setApiIsEnabled(false);
      } else {
        setWebhookMsg(t('integrationEnabled'));
        setApiIsEnabled(true);
      }
    },
  });

  const { mutateAsync: doUninstallBitrix, isPending: uninstallingBitrix } = useMutation({
    mutationFn: uninstallBitrix,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['bitrixStatus'] });
    },
  });

  // Open Channels de Bitrix24 (cuando está instalado)
  const {
    data: openChannelsResp,
    isLoading: isLoadingOpenChannels,
    isError: isErrorOpenChannels,
    error: openChannelsError,
  } = useQuery<OpenChannelsResponse>({
    queryKey: ['bitrixOpenChannels'],
    queryFn: getOpenChannels,
    enabled: isInstalled,
  });

  type OpenChannelOption = { id: string; name: string };
  const openChannels: OpenChannelOption[] = useMemo(() => {
    // Estructura esperada: { success: true, result: { result: [...] } } o { success: true, result: [...] }
    const base = openChannelsResp?.result as unknown;
    let arrUnknown: unknown[] = [];
    if (typeof base === 'object' && base !== null && Array.isArray((base as { result?: unknown[] }).result)) {
      arrUnknown = (base as { result: unknown[] }).result ?? [];
    } else if (Array.isArray(base)) {
      arrUnknown = base as unknown[];
    }
    const toOption = (item: unknown): OpenChannelOption => {
      const obj = (item ?? {}) as Record<string, unknown>;
      const idVal = (obj.ID as string | number | undefined) ?? (obj.id as string | number | undefined) ?? '';
      const nameVal = (obj.LINE_NAME as string | undefined) ?? (obj.NAME as string | undefined) ?? (obj.name as string | undefined);
      const id = String(idVal);
      const name = nameVal ?? `${t('line') || 'Line'} ${id}`;
      return { id, name };
    };
    return arrUnknown.map(toOption);
  }, [openChannelsResp, t]);

  // When a line is selected, fetch current linkage to prefill channel
  const { data: currentLink, refetch: refetchCurrentLink } = useQuery({
    queryKey: ['bxOpenChannelLink', selectedLine],
    queryFn: () => getOpenChannelLink({ line: selectedLine }),
    enabled: isInstalled && !!selectedLine,
  });

  React.useEffect(() => {
    const linked = currentLink?.data;
    console.log('[LINK STATE] currentLink cambió:', {
      hasData: !!linked,
      linked: linked?.linked,
      channelId: linked?.channelId,
      channelName: linked?.channel?.nameChannel,
      isActive: linked?.isActive,
      selectedLine,
    });
    // Only auto-update selectedChannelId if the line change was not manual
    if (linked?.linked && linked.channelId && !isManualLineChangeRef.current) {
      isManualChannelChangeRef.current = true;
      setSelectedChannelId(linked.channelId);
    }
    // Reset the manual flag after processing
    isManualLineChangeRef.current = false;
  }, [currentLink, selectedLine]);

  // When a channel is selected, find and select the Open Channel linked to it
  React.useEffect(() => {
    // Skip auto-search if the channel change was automatic (from line selection)
    if (isManualChannelChangeRef.current || !isInstalled || !selectedChannelId || openChannels.length === 0) {
      isManualChannelChangeRef.current = false;
      return;
    }
    
    let cancelled = false;
    (async () => {
      // Search through all Open Channels to find which one is linked to this channelId
      for (const oc of openChannels) {
        try {
          const res = await getOpenChannelLink({ line: oc.id });
          if (!cancelled && res?.data?.linked && res.data.channelId === selectedChannelId) {
            // Found the Open Channel linked to this channel
            isManualLineChangeRef.current = true;
            setSelectedLine(oc.id);
            return;
          }
        } catch (err) {
          console.debug('Error checking Open Channel link:', err);
        }
      }
      // If no link found, clear the selected line or keep the current one
      // We keep the current selectedLine to allow creating new links
    })();
    
    return () => { cancelled = true; };
  }, [selectedChannelId, openChannels, isInstalled]);

  // Auto-select a line after load: pick the first with a saved link or fallback to the first available
  React.useEffect(() => {
    if (!isInstalled) return;
    if (!selectedLine && openChannels.length > 0) {
      let cancelled = false;
      (async () => {
        // Try to find a linked line
        for (const oc of openChannels) {
          try {
            const res = await getOpenChannelLink({ line: oc.id });
            if (!cancelled && res?.data?.linked) {
              setSelectedLine(oc.id);
              return;
            }
          } catch (err) {
            console.debug('Ignorando error al verificar vínculo inicial de Open Channel:', err);
          }
        }
        if (!cancelled) setSelectedLine(openChannels[0].id);
      })();
      return () => { cancelled = true; };
    }
  }, [isInstalled, openChannels, selectedLine]);

  // Cargar canales reales
  const {
    data: channels = [],
    isLoading: isLoadingChannels,
    isError: isErrorChannels,
    error: channelsError,
  } = useQuery({
    queryKey: ['channels'],
    queryFn: getChannels,
  });

  // Tour functions
  const startTour = () => {
    const steps = [];

    // Always add the page title step
    steps.push({
      element: '#integrations-page-title',
      popover: {
        title: t('integrations'),
        description: t('tourIntegrationsPageDesc'),
        side: 'bottom',
        align: 'start',
      },
    });

    if (!isInstalled && !apiExists) {
      // Main integrations cards view
      steps.push({
        element: '#integrations-grid',
        popover: {
          title: t('availableIntegrations'),
          description: t('tourIntegrationsGridDesc'),
          side: 'top',
          align: 'start',
        },
      });

      steps.push({
        element: '#bitrix24-card',
        popover: {
          title: t('bitrix24Integration'),
          description: t('tourBitrix24CardDesc'),
          side: 'left',
          align: 'start',
        },
      });

      steps.push({
        element: '#whatsapp-api-card',
        popover: {
          title: t('whatsappApiIntegration'),
          description: t('tourWhatsappApiCardDesc'),
          side: 'right',
          align: 'start',
        },
      });
    } else if (isInstalled) {
      // Bitrix24 connected view
      steps.push({
        element: '#bitrix-connected-header',
        popover: {
          title: t('bitrix24Connected'),
          description: t('tourBitrixConnectedHeaderDesc'),
          side: 'bottom',
          align: 'start',
        },
      });

      steps.push({
        element: '#channels-list',
        popover: {
          title: t('channels'),
          description: t('tourChannelsListDescIntegration'),
          side: 'right',
          align: 'start',
        },
      });

      steps.push({
        element: '#channel-settings',
        popover: {
          title: t('channelSettings'),
          description: t('tourChannelSettingsDesc'),
          side: 'left',
          align: 'start',
        },
      });

      steps.push({
        element: '#open-channel-selector',
        popover: {
          title: t('openChannel'),
          description: t('tourOpenChannelSelectorDesc'),
          side: 'bottom',
          align: 'start',
        },
      });

      steps.push({
        element: '#add-to-open-channel-toggle',
        popover: {
          title: t('addToOpenChannel'),
          description: t('tourAddToOpenChannelToggleDesc'),
          side: 'top',
          align: 'start',
        },
      });
    } else if (apiExists) {
      // WhatsApp API view
      steps.push({
        element: '#api-connected-header',
        popover: {
          title: t('whatsappApiActive'),
          description: t('tourApiConnectedHeaderDesc'),
          side: 'bottom',
          align: 'start',
        },
      });

      steps.push({
        element: '#api-token-field',
        popover: {
          title: t('apiToken'),
          description: t('tourApiTokenDesc'),
          side: 'bottom',
          align: 'start',
        },
      });

      steps.push({
        element: '#webhook-secret-field',
        popover: {
          title: t('webhookSecret'),
          description: t('tourWebhookSecretDesc'),
          side: 'bottom',
          align: 'start',
        },
      });

      steps.push({
        element: '#webhook-url-field',
        popover: {
          title: t('webhookUrl'),
          description: t('tourWebhookUrlDesc'),
          side: 'top',
          align: 'start',
        },
      });

      steps.push({
        element: '#webhook-logs',
        popover: {
          title: t('webhookLogs'),
          description: t('tourWebhookLogsDesc'),
          side: 'top',
          align: 'start',
        },
      });
    }

    driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps,
    }).drive();
  };

  // Vista conectada (diseño distinto al ejemplo pero con funciones equivalentes)
  const ConnectedView = useMemo(() => {
  if (!isInstalled) return null;

    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between" id="bitrix-connected-header">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{t('bitrix24Connected')}</h2>
              <p className="text-sm text-gray-600 flex items-center gap-2">
                <Link2 className="h-4 w-4" /> {bitrixStatus?.domain}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={startTour}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
              title={t('startTour')}
            >
              <HelpCircle className="h-4 w-4" />
            </button>
            <button className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50">
              <RefreshCcw className="h-4 w-4" /> {t('updateCrmData')}
            </button>
            <button className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50">
              <ShieldCheck className="h-4 w-4" /> {t('viewRecords')}
            </button>
            <button
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => { if (confirm(t('confirmDeleteBitrix'))) { doUninstallBitrix(); } }}
              disabled={uninstallingBitrix}
            >
              {uninstallingBitrix ? t('deleting') : t('deleteIntegration')}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista de canales reales */}
          <aside id="channels-list" className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('channels')}</h3>
            {isLoadingChannels && (
              <div className="text-sm text-gray-500">{t('loadingChannelsEllipsis')}</div>
            )}
            {isErrorChannels && (
              <div className="text-sm text-red-600">
                {t('errorLoadingChannelsDetail', { error: (channelsError as { message?: string })?.message || t('unexpectedError') })}
              </div>
            )}
            {!isLoadingChannels && !isErrorChannels && (
              <ul className="space-y-2">
                {channels.length === 0 ? (
                  <li className="text-sm text-gray-500">{t('noChannelsConfigured')}</li>
                ) : (
                  channels.map((c) => {
                    const isSelected = selectedChannelId === c.id;
                    const linked = currentLink?.data?.linked;
                    const linkedChannelId = currentLink?.data?.channelId;
                    const isLinkedToSelectedLine = !!(linked && linkedChannelId === c.id);
                    return (
                      <li
                        key={c.id}
                        className={`flex items-center justify-between group cursor-pointer rounded-md px-2 py-2 border ${isSelected ? 'bg-emerald-50 border-emerald-200' : 'border-transparent hover:bg-gray-50'}`}
                        onClick={() => {
                          isManualChannelChangeRef.current = false; // Reset flag to allow search
                          setSelectedChannelId(c.id);
                        }}
                        title={isLinkedToSelectedLine ? t('linked') : undefined}
                      >
                        <div className="min-w-0">
                          <p className={`text-sm truncate ${isSelected ? 'text-emerald-700' : 'text-gray-700 group-hover:text-gray-900'}`}>{c.nameChannel}</p>
                          <p className="text-[11px] text-gray-500 truncate">{c.phoneChannel}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {isLinkedToSelectedLine && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">{t('linked')}</span>
                          )}
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${c.subscriptionStatus === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                            {c.subscriptionStatus === 'active' ? t('active') : t('inactive')}
                          </span>
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
            )}
          </aside>

          {/* Ajustes del canal */}
          <section id="channel-settings" className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="h-5 w-5 text-gray-500" />
              <h3 className="text-base font-semibold text-gray-900">{t('channelSettings')}</h3>
            </div>

            <div className="space-y-6">
              {/* Selector de Open Channel - Siempre visible cuando hay canales disponibles */}
              <div id="open-channel-selector">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('openChannel')}</label>
                {isLoadingOpenChannels && (
                  <div className="text-sm text-gray-500">{t('loadingOpenChannels')}</div>
                )}
                {isErrorOpenChannels && (
                  <div className="text-sm text-red-600">{t('errorLoadingOpenChannels', { error: (openChannelsError as { message?: string })?.message || t('unexpectedError') })}</div>
                )}
                {!isLoadingOpenChannels && !isErrorOpenChannels && (
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={selectedLine}
                    onChange={(e) => {
                      isManualLineChangeRef.current = true;
                      setSelectedLine(e.target.value);
                      setSaveMsg(null);
                      setPendingChange(null);
                    }}
                  >
                    <option value="">{t('selectChannel')}</option>
                    {openChannels.map((oc: OpenChannelOption) => (
                      <option key={oc.id} value={oc.id}>{oc.name}</option>
                    ))}
                  </select>
                )}
                {currentLink?.data?.linked && currentLink?.data?.channel && (
                  <p className="mt-2 text-xs text-gray-600">
                    {t('currentlyLinkedTo')} <span className="font-medium">{currentLink.data.channel.nameChannel}</span> ({currentLink.data.channel.phoneChannel})
                  </p>
                )}
              </div>

              {/* Toggle: Añadir a Open Channel */}
              <div id="add-to-open-channel-toggle" className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{t('addToOpenChannel')}</p>
                  <p className="text-xs text-gray-500">{t('messagesWillBeRouted')}</p>
                </div>
                {(() => {
                  const active = currentLink?.data?.isActive === true;
                  const canToggle = !!(selectedLine && currentLink?.data?.linked);
                  return (
                    <button
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${active ? 'bg-emerald-500' : 'bg-gray-300'} ${!canToggle || toggling ? 'opacity-60 cursor-not-allowed' : ''}`}
                      disabled={!canToggle || toggling}
                      onClick={async () => {
                        if (!canToggle || !selectedLine) return;
                        try {
                          setToggling(true);
                          await setOpenChannelActive({ line: selectedLine, isActive: !active });
                          await refetchCurrentLink();
                        } finally {
                          setToggling(false);
                        }
                      }}
                      title={canToggle ? (active ? t('deactivate') : t('activate')) : t('linkChannelFirst')}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${active ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  );
                })()}
              </div>

              {saveMsg && (
                <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">{saveMsg}</div>
              )}

              {pendingChange && (
                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                  <p className="mb-2">{pendingChange.message}</p>
                  <div className="flex gap-2 justify-end">
                    <button
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
                      onClick={() => setPendingChange(null)}
                    >{t('cancelAction')}</button>
                    <button
                      className="px-3 py-1.5 text-sm text-white bg-amber-600 rounded-md hover:bg-amber-700"
                      onClick={async () => {
                        if (!selectedLine || !selectedChannelId) return;
                        console.log('[CONFIRM DEBUG] Confirmando cambio:', {
                          selectedLine,
                          selectedChannelId,
                          confirm: true,
                        });
                        const res = await saveOpenChannelLink({ line: selectedLine, channelId: Number(selectedChannelId), confirm: true });
                        console.log('[CONFIRM DEBUG] Respuesta de confirmación:', {
                          linked: res.data.linked,
                          updated: res.data.updated,
                          message: res.data.message,
                          fullResponse: res.data,
                        });
                        if (res.data.linked) {
                          setPendingChange(null);
                          setSaveMsg(t('linkUpdated'));
                          refetchCurrentLink();
                        }
                      }}
                    >{t('confirmChange')}</button>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <SaveButton
                  disabled={!selectedLine || !selectedChannelId}
                  onClick={async () => {
                    if (!selectedLine || !selectedChannelId) return;
                    console.log('[SAVE DEBUG] Iniciando guardado:', {
                      selectedLine,
                      selectedChannelId,
                      currentLinkedChannelId: currentLink?.data?.channelId,
                      currentLinked: currentLink?.data?.linked,
                    });
                    setSaveMsg(null);
                    const res: SaveOpenChannelLinkResponse = await saveOpenChannelLink({ line: selectedLine, channelId: Number(selectedChannelId) });
                    console.log('[SAVE DEBUG] Respuesta del servidor:', {
                      requiresConfirm: res.data.requiresConfirm,
                      linked: res.data.linked,
                      updated: res.data.updated,
                      message: res.data.message,
                      fullResponse: res.data,
                    });
                    if (res.data.requiresConfirm) {
                      console.log('[SAVE DEBUG] Mostrando confirmación');
                      setPendingChange({ message: res.data.message || t('confirmationRequired'), line: selectedLine, channelId: Number(selectedChannelId) });
                    } else if (res.data.linked) {
                      console.log('[SAVE DEBUG] Guardado sin confirmación');
                      setSaveMsg(res.data.updated ? t('linkUpdated') : t('linkCreated'));
                      await refetchCurrentLink();
                    }
                  }}
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }, [
    t,
    isInstalled,
    bitrixStatus,
    channels,
    isLoadingChannels,
    isErrorChannels,
    channelsError,
    isLoadingOpenChannels,
    isErrorOpenChannels,
    openChannelsError,
    openChannels,
  selectedLine,
  selectedChannelId,
  pendingChange,
  saveMsg,
  refetchCurrentLink,
  currentLink?.data?.linked,
  currentLink?.data?.channelId,
  currentLink?.data?.channel,
  currentLink?.data?.isActive,
  toggling,
  doUninstallBitrix,
  uninstallingBitrix,
  ]);

  return (
    <div className="min-h-screen bg-gray-50">

  {statusLoading || apiStatusLoading ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center text-gray-600">{t('loadingBitrixStatus')}</div>
      ) : isInstalled ? (
        ConnectedView
  ) : apiExists ? (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6 flex items-center justify-between" id="api-connected-header">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{t('whatsappApiActive')}</h2>
                <p className="text-sm text-gray-600">{t('useTokenAndWebhook')}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={startTour}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                title={t('startTour')}
              >
                <HelpCircle className="h-4 w-4" />
              </button>
              <button
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                onClick={async () => { await doRotateKey(); }}
                disabled={!!rotatingKey}
              >
                <RefreshCcw className="h-4 w-4" /> {rotatingKey ? t('renewingKey') : t('renewApiKey')}
              </button>
              <button
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                onClick={async () => { await doRotateWebhookSecret(); }}
                disabled={!!rotatingSecret}
              >
                <RefreshCcw className="h-4 w-4" /> {rotatingSecret ? t('rotating') : t('rotateWebhookSecret')}
              </button>
              <button
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                onClick={async () => { await doSetEnabled(!apiIsEnabled); }}
                disabled={!!togglingEnabled}
              >{togglingEnabled ? t('updating') : (apiIsEnabled ? t('disable') : t('enable'))}</button>
              <button
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => { if (confirm(t('confirmDeleteApi'))) { doDeleteApi(); } }}
                disabled={deletingApi}
              >{deletingApi ? t('deletingIntegration') : t('deleteIntegration')}</button>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
            <div id="api-token-field">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('apiToken')}</label>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  type={isApiKeyMasked ? 'password' : 'text'}
                  value={apiInfo?.apiKey || ''}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                {!isApiKeyMasked && (
                  <button className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50" onClick={() => navigator.clipboard.writeText(apiInfo?.apiKey || '')}>
                    <Copy className="h-4 w-4"/> {t('copy')}
                  </button>
                )}
              </div>
              {isApiKeyMasked && (
                <p className="text-xs text-gray-500 mt-1">{t('secretMaskedHint') || t('renewApiKey')}</p>
              )}
            </div>
            <div id="webhook-secret-field">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('webhookSecret')}</label>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  type={!isWebhookSecretMasked && showSecret ? 'text' : 'password'}
                  value={apiInfo?.webhookSecret || ''}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                {!isWebhookSecretMasked && (
                  <button className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50" onClick={() => setShowSecret((s) => !s)}>
                    {showSecret ? t('hide') : t('show')}
                  </button>
                )}
                {!isWebhookSecretMasked && (
                  <button className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50" onClick={() => navigator.clipboard.writeText(apiInfo?.webhookSecret || '')}>
                    <Copy className="h-4 w-4"/> {t('copy')}
                  </button>
                )}
              </div>
              {isWebhookSecretMasked && (
                <p className="text-xs text-gray-500 mt-1">{t('secretMaskedHint') || t('rotateWebhookSecret')}</p>
              )}
            </div>
            <div id="webhook-url-field">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('webhookUrl')}</label>
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  placeholder={t('webhookUrlPlaceholder')}
                  value={webhookInput}
                  onChange={(e) => {
                    setWebhookInput(e.target.value);
                    setWebhookMsg(null);
                  }}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                <button
                  className="px-3 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50"
                  disabled={!webhookInput.trim() || savingWebhook}
                  onClick={async () => {
                    setWebhookMsg(null);
                    // validación mínima
                    try {
                      const u = new URL(webhookInput.trim());
                      if (u.protocol !== 'https:') {
                        setWebhookMsg(t('webhookMustUseHttps'));
                        return;
                      }
                    } catch {
                      setWebhookMsg(t('enterValidUrl'));
                      return;
                    }
                    await doSetWebhook(webhookInput.trim());
                  }}
                >{savingWebhook ? t('saving') : t('save')}</button>
                <button
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                  onClick={async () => { await doSetWebhook(null); }}
                  disabled={savingWebhook}
                >{t('removeUrl')}</button>
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('defineUrlToReceive')}</p>
              {webhookMsg && (
                <p className={`mt-2 text-sm ${webhookMsg.includes('correctamente') || webhookMsg.includes('eliminado') || webhookMsg.includes(t('webhookSavedSuccessfully')) || webhookMsg.includes(t('webhookRemoved')) ? 'text-emerald-700' : 'text-red-600'}`}>{webhookMsg}</p>
              )}
            </div>
            <div id="webhook-logs">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">{t('webhookLogs')}</label>
                <button className="text-sm text-gray-600 hover:text-gray-900" onClick={() => { /* refetch will run automatically on queryKey change */ setLogsPage(1); }}>
                  {t('refresh')}
                </button>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <select className="border border-gray-300 rounded-md px-2 py-1 text-sm" value={logsEventType} onChange={(e) => { setLogsEventType(e.target.value); setLogsPage(1); }}>
                  <option value="">{t('allEvents')}</option>
                  <option value="message.incoming">message.incoming</option>
                  <option value="message.automation">message.automation</option>
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600">
                      <th className="px-2 py-1">{t('date')}</th>
                      <th className="px-2 py-1">{t('event')}</th>
                      <th className="px-2 py-1">{t('attempt')}</th>
                      <th className="px-2 py-1">{t('status')}</th>
                      <th className="px-2 py-1">{t('code')}</th>
                      <th className="px-2 py-1">{t('nextAttempt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(logsResp?.data?.items || []).map((row) => (
                      <tr key={`${row.id}`} className="border-t">
                        <td className="px-2 py-1 whitespace-nowrap">{new Date(row.createdAt).toLocaleString()}</td>
                        <td className="px-2 py-1">{row.eventType}</td>
                        <td className="px-2 py-1">{row.attempt}</td>
                        <td className="px-2 py-1">{row.success ? t('ok') : (row.error ? t('error') : t('pending'))}</td>
                        <td className="px-2 py-1">{row.status ?? '-'}</td>
                        <td className="px-2 py-1">{row.nextAttemptAt ? new Date(row.nextAttemptAt).toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-2 flex items-center gap-2">
                  <button className="px-2 py-1 border rounded disabled:opacity-50" disabled={logsPage <= 1} onClick={() => setLogsPage((p) => Math.max(1, p - 1))}>{t('previous')}</button>
                  <span className="text-xs text-gray-600">{t('page')} {logsPage}</span>
                  <button className="px-2 py-1 border rounded disabled:opacity-50" disabled={(logsResp?.data?.items?.length || 0) < 20} onClick={() => setLogsPage((p) => p + 1)}>{t('next')}</button>
                </div>
              </div>
            </div>
            <div>
              <a href="https://wazzup24.com/help/api-en/sending-messages/" target="_blank" className="inline-flex items-center text-sm text-green-600 hover:text-green-700 font-medium">
                {t('viewDocumentation')} <ExternalLink className="ml-1 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6 flex items-center justify-between" id="integrations-page-title">
            <h1 className="text-2xl font-semibold text-gray-900">{t('integrations')}</h1>
            <button
              onClick={startTour}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
              title={t('startTour')}
            >
              <HelpCircle className="h-4 w-4" /> {t('help')}
            </button>
          </div>
          <div id="integrations-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {integrations.map((integration) => (
              <div
                id={`${integration.id}-card`}
                key={integration.id}
                className={`bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200 ${
                  (integration.id === 'bitrix24' && apiExists) || (integration.id === 'whatsapp-api' && isInstalled)
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer'
                }`}
                onClick={() => handleConnect(integration.id)}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-semibold text-blue-600">
                      {integration.name}
                    </h3>
                  </div>
                  <Info className="h-5 w-5 text-gray-400" />
                </div>
                <img className='w-full mb-6' src={integration.img} />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {integration.isAvailable ? t('available') : t('comingSoon')}
                  </span>
          {integration.id === 'whatsapp-api' && apiExists ? (
                    <div className="flex items-center gap-2">
            <span className={`text-xs ${apiIsEnabled ? 'text-emerald-600' : 'text-gray-600'}`}>{apiIsEnabled ? t('enabled') : t('disabled')}</span>
                      <button
                        className="text-red-600 hover:text-red-700 text-xs font-medium"
                        onClick={(e) => { e.stopPropagation(); doDeleteApi(); }}
                        disabled={deletingApi}
                      >{t('delete')}</button>
                    </div>
                  ) : integration.id === 'bitrix24' && isInstalled ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-emerald-600">{t('installed')}</span>
                      <button
                        className="text-red-600 hover:text-red-700 text-xs font-medium"
                        onClick={(e) => { e.stopPropagation(); doUninstallBitrix(); }}
                        disabled={uninstallingBitrix}
                      >{t('delete')}</button>
                    </div>
                  ) : (
                    <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                      {t('connect')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bitrix24 Setup Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCancel}
            ></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-6 pt-6 pb-4">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('connectionToBitrix24')}
                  </h3>
                  <button
                    onClick={handleCancel}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Step 1 */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">{t('step1')}</h4>
                    <p className="text-gray-600 text-sm">
                      {t('step1InstallLocalApp')}
                    </p>
                  </div>

                  {/* Step 2 */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">{t('step2')}</h4>
                    <p className="text-gray-600 text-sm">
                      {t('step2CopyCredentials')}
                    </p>
                  </div>

                  {/* Step 3 */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">{t('step3')}</h4>
                    <p className="text-gray-600 text-sm mb-4">
                      {t('step3PasteCredentials')}
                    </p>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('bitrixAccountLink')}</label>
                        <input
                          type="url"
                          value={bitrixUrl}
                          onChange={(e) => setBitrixUrl(e.target.value)}
                          placeholder="https://myaccount.bitrix24.com"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('applicationId')}</label>
                        <input
                          type="text"
                          value={clientId}
                          onChange={(e) => setClientId(e.target.value)}
                          placeholder={t('applicationIdPlaceholder')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('applicationKey')}</label>
                        <input
                          type="password"
                          value={clientSecret}
                          onChange={(e) => setClientSecret(e.target.value)}
                          placeholder={t('applicationKeyPlaceholder')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        {t('credentialsInfo')}
                      </p>
                      {message && (
                        <p className="text-xs text-red-600">{message}</p>
                      )}
                      {error && (
                        <p className="text-xs text-red-600">{error}</p>
                      )}
                    </div>
                  </div>

                  {/* Help Link */}
                  <div>
                    <Link
                      to="/how-to-connect-with-bx24"
                      className="inline-flex items-center text-sm text-green-600 hover:text-green-700 font-medium"
                    >
                      {t('howToConnectBitrix')}
                      <ExternalLink className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  {t('cancel').toUpperCase()}
                </button>
                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={!bitrixUrl.trim() || !clientId.trim() || !clientSecret.trim() || validating}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-600 border border-transparent rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {validating ? t('validating').toUpperCase() : t('continueAction').toUpperCase()}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Setup Modal */}
      {showApiModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowApiModal(false)}></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-6 pt-6 pb-4">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">{t('activateWhatsappApi')}</h3>
                  <button onClick={() => setShowApiModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <div className="space-y-4 text-sm text-gray-700">
                  <p>{t('apiIntegrationDescription')}</p>
                  <p>{t('afterActivationInstructions')}</p>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3">
                <button type="button" onClick={() => setShowApiModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">{t('cancel')}</button>
                <button type="button" onClick={() => doEnableApi()} disabled={enablingApi} className="px-4 py-2 text-sm font-medium text-white bg-gray-600 border border-transparent rounded-md hover:bg-gray-700 disabled:opacity-50">
                  {enablingApi ? t('activating') : t('activateApi')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMIntegrations;

function SaveButton({ disabled, onClick }: { disabled?: boolean; onClick: () => void | Promise<void> }) {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  return (
    <button
      className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
      disabled={disabled || loading}
      onClick={async () => {
        try { setLoading(true); await onClick(); } finally { setLoading(false); }
      }}
    >{loading ? t('saving') : t('save')}</button>
  );
}